import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { Search, Filter, Loader2, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateRangePicker } from '../components/ui/date-range-picker';
import { TagListPreview } from '../components/ui/tag-list-preview';

const TABS = [
  { id: 'transcribed', label: 'Transcribe & Translate' },
  { id: 'analyzed', label: 'Analyzed' },
];

const EMPTY_ANALYZED_FILTERS = {
  direction: '',
  agent_name: '',
  detected_language: '',
  escalation: '',
  score_min: '',
  score_max: '',
  score_range: '',
  from_date: '',
  to_date: '',
};

const EMPTY_TRANSCRIBED_FILTERS = {
  direction: '',
  agent_name: '',
  processing_status: '',
  from_date: '',
  to_date: '',
};

const toIsoDate = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return '';
  }
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function getScoreBadge(score) {
  if (score === null || score === undefined) return { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', label: 'N/A' };
  if (score >= 90) return { cls: 'score-excellent', label: 'Excellent' };
  if (score >= 75) return { cls: 'score-good', label: 'Good' };
  if (score >= 60) return { cls: 'score-review', label: 'Needs Review' };
  return { cls: 'score-critical', label: 'Critical' };
}

function formatLanguage(value) {
  const key = String(value || '').trim().toLowerCase();
  const labels = {
    te: 'Telugu',
    ta: 'Tamil',
    hi: 'Hindi',
    ml: 'Malayalam',
    kn: 'Kannada',
    other: 'Other',
  };
  return labels[key] || 'Other';
}

function getAgentMatchRank(agentName, query) {
  const normalizedName = String(agentName || '').trim().toLowerCase();
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return 0;
  if (normalizedName === normalizedQuery) return 0;
  if (normalizedName.startsWith(normalizedQuery)) return 1;
  const matchIndex = normalizedName.indexOf(normalizedQuery);
  return matchIndex >= 0 ? matchIndex + 2 : Number.MAX_SAFE_INTEGER;
}

export default function ProcessedCallsPage() {
  const navigate = useNavigate();
  const { isAdmin, isSupervisor } = useAuth();
  const canViewProcessed = isAdmin || isSupervisor;

  const [activeTab, setActiveTab] = useState(isAdmin || isSupervisor ? 'transcribed' : 'analyzed');
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [analyzingCalls, setAnalyzingCalls] = useState(new Set());
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableAgentNames, setAvailableAgentNames] = useState([]);
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  const [agentSuggestionSource, setAgentSuggestionSource] = useState(null);
  const [transcribedSearch, setTranscribedSearch] = useState('');
  const [analyzedSearch, setAnalyzedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ from_date: '', to_date: '' });
  const [analyzedFilters, setAnalyzedFilters] = useState(EMPTY_ANALYZED_FILTERS);
  const [transcribedFilters, setTranscribedFilters] = useState(EMPTY_TRANSCRIBED_FILTERS);
  const [transcribedSortBy, setTranscribedSortBy] = useState('created_at');
  const [transcribedSortOrder, setTranscribedSortOrder] = useState('desc');
  const [callTagMap, setCallTagMap] = useState({});
  const [callTagsLoadingKey, setCallTagsLoadingKey] = useState('');
  const analyzedRequestKeyRef = useRef('');
  const callTagsRequestKeyRef = useRef('');

  const LIMIT = 20;

  const fetchTranscribedCalls = useCallback(async (params = {}) => {
    if (!canViewProcessed) return;
    setLoading(true);
    try {
      const res = await api.getPendingAnalysisCalls({ limit: 500, offset: 0, ...params });
      setCalls(res.calls || []);
    } catch {
      toast.error('Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [canViewProcessed]);

  const fetchAnalyzedCalls = useCallback(async (params) => {
    if (!canViewProcessed) return;
    setLoading(true);
    try {
      const res = await api.getCalls(params);
      setCalls(res.calls || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch {
      toast.error('Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [canViewProcessed]);

  const analyzedParams = useMemo(() => {
    const params = {
      page,
      limit: LIMIT,
      status: 'completed',
      search: analyzedSearch.trim(),
      sort_by: sortBy,
      sort_order: sortOrder,
      ...analyzedFilters,
      ...(analyzedFilters.escalation && { escalation_detected: analyzedFilters.escalation }),
      from_date: dateRange.from_date,
      to_date: dateRange.to_date || dateRange.from_date,
    };

    // Score range
    if (analyzedFilters.score_min) {
      params.min_score = Number(analyzedFilters.score_min);
    }
    if (analyzedFilters.score_max) {
      params.max_score = Number(analyzedFilters.score_max);
    }

    // Score range
    if (analyzedFilters.score_range) {
      const [minStr, maxStr] = analyzedFilters.score_range.split('-');
      params.min_score = Number(minStr);
      params.max_score = Number(maxStr);
    }

    return params;
  }, [analyzedFilters, analyzedSearch, dateRange, page, sortBy, sortOrder]);

  useEffect(() => {
    if (activeTab !== 'transcribed') {
      return;
    }

    const params = {
      from_date: dateRange.from_date,
      to_date: dateRange.to_date || dateRange.from_date,
    };
    fetchTranscribedCalls(params);
  }, [activeTab, dateRange, fetchTranscribedCalls]);

  useEffect(() => {
    if (activeTab !== 'analyzed') {
      return;
    }

    const requestKey = JSON.stringify(analyzedParams);
    const timeoutId = setTimeout(() => {
      if (requestKey === analyzedRequestKeyRef.current) {
        return;
      }

      analyzedRequestKeyRef.current = requestKey;
      fetchAnalyzedCalls(analyzedParams);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [activeTab, analyzedParams, fetchAnalyzedCalls]);

  useEffect(() => {
    if (!canViewProcessed) return;
    api.getCallLanguages().then((res) => setAvailableLanguages(res.languages || [])).catch(() => { });
  }, [canViewProcessed]);

  const handleAnalyzeCall = async (e, callId) => {
    e.stopPropagation();
    setAnalyzingCalls((prev) => new Set(prev).add(callId));
    try {
      const res = await api.analyzeCall(callId);
      toast.success(res.message || 'Analysis started');
      setTimeout(fetchTranscribedCalls, 3000);
    } catch (err) {
      toast.error(err?.message || 'Failed to start analysis');
    } finally {
      setAnalyzingCalls((prev) => { const n = new Set(prev); n.delete(callId); return n; });
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
    setDateRange({ from_date: '', to_date: '' });
    setTranscribedSearch('');
    setAnalyzedSearch('');
    setAnalyzedFilters(EMPTY_ANALYZED_FILTERS);
    setTranscribedFilters(EMPTY_TRANSCRIBED_FILTERS);
    setTranscribedSortBy('created_at');
    setTranscribedSortOrder('desc');
    analyzedRequestKeyRef.current = '';
    setShowFilters(false);
    setShowAgentSuggestions(false);
    setAgentSuggestionSource(null);
  };

  useEffect(() => {
    if (!canViewProcessed) return;
    api.getAgentsList()
      .then((res) => {
        const names = Array.isArray(res?.agents)
          ? res.agents
            .map((a) => (typeof a === 'object' ? (a.agent_name || a.agent_number || '') : String(a)))
            .map((name) => String(name || '').trim())
            .filter(Boolean)
          : [];

        const uniqueNames = Array.from(new Map(
          names.map((name) => [name.toLowerCase(), name])
        ).values()).sort((left, right) => left.localeCompare(right));

        setAvailableAgentNames(uniqueNames);
      })
      .catch(() => { });
  }, [canViewProcessed]);

  const agentNameQuery = (() => {
    if (agentSuggestionSource === 'transcribed') {
      return transcribedSearch.trim().toLowerCase();
    }

    if (agentSuggestionSource === 'analyzed-search') {
      return analyzedSearch.trim().toLowerCase();
    }

    if (agentSuggestionSource === 'analyzed-filter') {
      return analyzedFilters.agent_name.trim().toLowerCase();
    }

    if (agentSuggestionSource === 'transcribed-filter') {
      return transcribedFilters.agent_name.trim().toLowerCase();
    }

    if (activeTab === 'transcribed') {
      return transcribedSearch.trim().toLowerCase();
    }

    return analyzedFilters.agent_name.trim().toLowerCase();
  })();
  const agentNameSuggestions = availableAgentNames
    .filter((name) => {
      if (!agentNameQuery) return true;
      return String(name).toLowerCase().includes(agentNameQuery);
    })
    .sort((left, right) => {
      const leftRank = getAgentMatchRank(left, agentNameQuery);
      const rightRank = getAgentMatchRank(right, agentNameQuery);
      if (leftRank !== rightRank) return leftRank - rightRank;
      return String(left).localeCompare(String(right));
    })
    .slice(0, 8);

  const handleAgentNameSelect = (name) => {
    if (agentSuggestionSource === 'transcribed') {
      setTranscribedSearch(name);
    } else if (agentSuggestionSource === 'analyzed-search') {
      setAnalyzedSearch(name);
    } else {
      setAnalyzedFilters((prev) => ({ ...prev, agent_name: name }));
    }
    setPage(1);
    setShowAgentSuggestions(false);
    setAgentSuggestionSource(null);
  };

  const handleRefresh = () => {
    if (activeTab === 'transcribed') {
      fetchTranscribedCalls();
      return;
    }

    analyzedRequestKeyRef.current = '';
    fetchAnalyzedCalls(analyzedParams);
  };

  const transcribedSearchQuery = transcribedSearch.trim().toLowerCase();

  const transcribedCalls = activeTab === 'transcribed'
    ? [...calls]
      .filter((call) => {
        if (transcribedSearchQuery) {
          const searchable = [
            call?.agent_name,
            call?.client_number,
            call?.direction,
            call?.date,
            call?.time,
            call?.processing_status,
          ];
          if (!searchable.some((value) => String(value || '').toLowerCase().includes(transcribedSearchQuery))) return false;
        }
        if (transcribedFilters.direction && call?.direction !== transcribedFilters.direction) return false;
        if (transcribedFilters.agent_name) {
          const q = transcribedFilters.agent_name.trim().toLowerCase();
          if (!String(call?.agent_name || '').toLowerCase().includes(q)) return false;
        }
        if (transcribedFilters.processing_status && call?.processing_status !== transcribedFilters.processing_status) return false;
        // Client-side date filter (fallback)
        const callDate = toIsoDate(new Date(call.date));
        const fromDate = dateRange.from_date;
        const toDate = dateRange.to_date;
        if (fromDate && callDate < fromDate) return false;
        if (toDate && callDate > toDate) return false;
        return true;
      })
      .sort((left, right) => {
        if (transcribedSearchQuery) {
          const leftRank = getAgentMatchRank(left?.agent_name, transcribedSearchQuery);
          const rightRank = getAgentMatchRank(right?.agent_name, transcribedSearchQuery);
          if (leftRank !== rightRank) return leftRank - rightRank;
        }
        const dir = transcribedSortOrder === 'asc' ? 1 : -1;
        if (transcribedSortBy === 'agent_name') {
          return dir * String(left?.agent_name || '').localeCompare(String(right?.agent_name || ''));
        }
        if (transcribedSortBy === 'call_duration') {
          return dir * ((Number(left?.call_duration) || 0) - (Number(right?.call_duration) || 0));
        }
        // default: created_at / date+time
        const leftDt = `${left?.date || ''} ${left?.time || ''}`;
        const rightDt = `${right?.date || ''} ${right?.time || ''}`;
        return dir * leftDt.localeCompare(rightDt);
      })
    : [];

  const transcribedTotal = transcribedCalls.length;
  const transcribedPages = Math.max(1, Math.ceil(transcribedTotal / LIMIT));
  const transcribedPage = Math.min(page, transcribedPages);
  const displayedCalls = activeTab === 'transcribed'
    ? transcribedCalls.slice((transcribedPage - 1) * LIMIT, transcribedPage * LIMIT)
    : calls;
  const displayedTotal = activeTab === 'transcribed' ? transcribedTotal : total;
  const displayedPages = activeTab === 'transcribed' ? transcribedPages : pages;
  const displayedPage = activeTab === 'transcribed' ? transcribedPage : page;
  const displayedCallIdsKey = displayedCalls.map((call) => call?.call_id).filter(Boolean).join('|');

  useEffect(() => {
    let cancelled = false;
    const displayedCallIds = displayedCallIdsKey ? displayedCallIdsKey.split('|') : [];

    if (displayedCallIds.length === 0) {
      setCallTagMap({});
      setCallTagsLoadingKey('');
      callTagsRequestKeyRef.current = '';
      return () => {
        cancelled = true;
      };
    }

    callTagsRequestKeyRef.current = displayedCallIdsKey;
    setCallTagsLoadingKey(displayedCallIdsKey);
    setCallTagMap({});

    api.getTagsForCalls(displayedCallIds)
      .then((res) => {
        if (cancelled || callTagsRequestKeyRef.current !== displayedCallIdsKey) {
          return;
        }

        const nextTagMap = Object.fromEntries(
          (res?.calls || []).map((entry) => [entry.call_id, Array.isArray(entry.tags) ? entry.tags : []])
        );
        setCallTagMap(nextTagMap);
        setCallTagsLoadingKey('');
      })
      .catch(() => {
        if (!cancelled && callTagsRequestKeyRef.current === displayedCallIdsKey) {
          setCallTagMap({});
          setCallTagsLoadingKey('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [displayedCallIdsKey]);

  if (!canViewProcessed) {
    return <div className="text-center py-12 text-muted-foreground">Admin or Supervisor access required</div>;
  }

  const tabLabel = activeTab === 'transcribed' ? 'Transcribed & Translated' : 'Analyzed';

  const openCallDetailsInNewTab = (callId, e) => {
    e.stopPropagation();
    const detailPath = `/calls/${encodeURIComponent(callId)}`;

    // Open first to avoid popup blockers, then hydrate auth state in the new tab.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      navigate(detailPath);
      return;
    }

    try {
      const token = sessionStorage.getItem('vcm_token');
      const userData = sessionStorage.getItem('vcm_user');
      if (token) {
        newTab.sessionStorage.setItem('vcm_token', token);
      }
      if (userData) {
        newTab.sessionStorage.setItem('vcm_user', userData);
      }
      newTab.location.href = detailPath;
    } catch (error) {
      newTab.location.href = detailPath;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Processed Calls</h1>
          <p className="text-sm text-muted-foreground mt-1">{tabLabel} ({displayedTotal} records)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {(isAdmin || isSupervisor ? TABS : TABS.filter((tab) => tab.id === 'analyzed')).map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-end gap-3">
        {activeTab === 'analyzed' && (
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Filter by agent or caller..."
              autoComplete="off"
              value={analyzedSearch}
              onFocus={() => {
                setAgentSuggestionSource('analyzed-search');
                setShowAgentSuggestions(true);
              }}
              onBlur={() => setTimeout(() => {
                setShowAgentSuggestions(false);
                setAgentSuggestionSource(null);
              }, 120)}
              onChange={(e) => {
                setAnalyzedSearch(e.target.value);
                setPage(1);
                setAgentSuggestionSource('analyzed-search');
                setShowAgentSuggestions(true);
              }}
              className="pl-9 bg-card"
            />
            {showAgentSuggestions && agentSuggestionSource === 'analyzed-search' && agentNameSuggestions.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                {agentNameSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAgentNameSelect(name);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'transcribed' && (
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Filter by agent or caller..."
              autoComplete="off"
              value={transcribedSearch}
              onFocus={() => {
                setAgentSuggestionSource('transcribed');
                setShowAgentSuggestions(true);
              }}
              onBlur={() => setTimeout(() => {
                setShowAgentSuggestions(false);
                setAgentSuggestionSource(null);
              }, 120)}
              onChange={(e) => {
                const nextValue = e.target.value;
                setTranscribedSearch(nextValue);
                setPage(1);
                setAgentSuggestionSource('transcribed');
                setShowAgentSuggestions(true);
              }}
              className="pl-9 bg-card"
            />
            {showAgentSuggestions && agentSuggestionSource === 'transcribed' && agentNameSuggestions.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                {agentNameSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAgentNameSelect(name);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'analyzed' && (
          <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-1" />
            Filters
          </Button>
        )}
        {activeTab === 'transcribed' && (
          <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-1" />
            Filters
          </Button>
        )}
      </div>

      {activeTab === 'analyzed' && showFilters && (
        <div className="bg-card border border-border rounded-xl p-4 grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-6 gap-3">
          <select
            value={analyzedFilters.direction}
            onChange={(e) => { setAnalyzedFilters({ ...analyzedFilters, direction: e.target.value }); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>

          <div className="relative">
            <Input
              type="text"
              placeholder="Agent name"
              autoComplete="off"
              value={analyzedFilters.agent_name}
              onFocus={() => {
                setAgentSuggestionSource('analyzed-filter');
                setShowAgentSuggestions(true);
              }}
              onBlur={() => setTimeout(() => {
                setShowAgentSuggestions(false);
                setAgentSuggestionSource(null);
              }, 120)}
              onChange={(e) => {
                const nextValue = e.target.value;
                setAnalyzedFilters((prev) => ({ ...prev, agent_name: nextValue }));
                setPage(1);
                setAgentSuggestionSource('analyzed-filter');
                setShowAgentSuggestions(true);
              }}
              className="bg-background"
            />
            {showAgentSuggestions && agentSuggestionSource === 'analyzed-filter' && agentNameSuggestions.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                {agentNameSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAgentNameSelect(name);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DateRangePicker
            value={dateRange}
            showValue={!!(dateRange.from_date || dateRange.to_date)}
            placeholder="Date Range"
            onChange={(range) => { setDateRange({ from_date: range.from_date, to_date: range.to_date }); setPage(1); }}
            onClear={() => { setDateRange({ from_date: '', to_date: '' }); setPage(1); }}
            buttonClassName="w-full min-w-0 h-9"
          />

          <select
            value={analyzedFilters.detected_language}
            onChange={(e) => { setAnalyzedFilters({ ...analyzedFilters, detected_language: e.target.value }); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Languages</option>
            {availableLanguages.map((lang) => (
              <option key={lang} value={lang}>{formatLanguage(lang)}</option>
            ))}
          </select>

          <select
            value={analyzedFilters.escalation}
            onChange={(e) => { setAnalyzedFilters({ ...analyzedFilters, escalation: e.target.value }); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Escalations</option>
            <option value="true">Detected</option>
            <option value="false">Not Detected</option>
          </select>

          <select
            value={analyzedFilters.score_range}
            onChange={(e) => { setAnalyzedFilters({ ...analyzedFilters, score_range: e.target.value }); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm">
            <option value="">All Scores</option>
            <option value="1-10">1-10</option>
            <option value="11-20">11-20</option>
            <option value="21-30">21-30</option>
            <option value="31-40">31-40</option>
            <option value="41-50">41-50</option>
            <option value="51-60">51-60</option>
            <option value="61-70">61-70</option>
            <option value="71-80">71-80</option>
            <option value="81-90">81-90</option>
            <option value="91-100">91-100</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="created_at">Sort: Time</option>
            <option value="score">Sort: Score</option>
            <option value="call_duration">Sort: Duration</option>
            <option value="agent_name">Sort: Agent</option>
            <option value="detected_language">Sort: Language</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAnalyzedFilters(EMPTY_ANALYZED_FILTERS);
              setPage(1);
              setDateRange({ from_date: '', to_date: '' });
            }}
          > Clear</Button>
        </div>
      )}

      {activeTab === 'transcribed' && showFilters && (
        <div className="bg-card border border-border rounded-xl p-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            value={transcribedFilters.direction}
            onChange={(e) => { setTranscribedFilters({ ...transcribedFilters, direction: e.target.value }); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>

          <div className="relative">
            <Input
              type="text"
              placeholder="Agent name"
              autoComplete="off"
              value={transcribedFilters.agent_name}
              onFocus={() => {
                setAgentSuggestionSource('transcribed-filter');
                setShowAgentSuggestions(true);
              }}
              onBlur={() => setTimeout(() => {
                setShowAgentSuggestions(false);
                setAgentSuggestionSource(null);
              }, 120)}
              onChange={(e) => {
                setTranscribedFilters((prev) => ({ ...prev, agent_name: e.target.value }));
                setPage(1);
                setAgentSuggestionSource('transcribed-filter');
                setShowAgentSuggestions(true);
              }}
              className="bg-background"
            />
            {showAgentSuggestions && agentSuggestionSource === 'transcribed-filter' && agentNameSuggestions.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                {agentNameSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setTranscribedFilters((prev) => ({ ...prev, agent_name: name }));
                      setPage(1);
                      setShowAgentSuggestions(false);
                      setAgentSuggestionSource(null);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DateRangePicker
            value={dateRange}
            showValue={!!(dateRange.from_date || dateRange.to_date)}
            placeholder="Date Range"
            onChange={(range) => { setDateRange({ from_date: range.from_date, to_date: range.to_date }); setPage(1); }}
            onClear={() => { setDateRange({ from_date: '', to_date: '' }); setPage(1); }}
            buttonClassName="w-full min-w-0 h-9"
          />

          <select
            value={transcribedSortBy}
            onChange={(e) => { setTranscribedSortBy(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="created_at">Sort: Time</option>
            <option value="call_duration">Sort: Duration</option>
            <option value="agent_name">Sort: Agent</option>
          </select>

          <select
            value={transcribedSortOrder}
            onChange={(e) => { setTranscribedSortOrder(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>

          <Button variant="ghost"
            onClick={() => {
              setTranscribedFilters(EMPTY_TRANSCRIBED_FILTERS);
              setTranscribedSortBy('created_at');
              setTranscribedSortOrder('desc');
              setPage(1);
              setDateRange({ from_date: '', to_date: '' });
            }}
          > Clear </Button>

        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          {activeTab === 'transcribed' ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Time</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Agent</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Caller</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Direction</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Duration</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Tags</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></td></tr>
                ) : displayedCalls.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-sm text-muted-foreground">No transcribed & translated calls pending analysis.</td></tr>
                ) : displayedCalls.map((call) => {
                  const callTags = callTagMap[call.call_id] || [];
                  const tagsLoading = callTagsLoadingKey === displayedCallIdsKey;
                  return (
                    <tr
                      key={call.call_id}
                      className="border-b border-border/50 hover:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={(e) => openCallDetailsInNewTab(call.call_id, e)}>
                      <td className="px-4 py-3">
                        <span className="text-sm">{call.date}</span>
                        <span className="text-xs text-muted-foreground ml-1">{call.time}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">{call.agent_name}</td>
                      <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{call.client_number}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>
                          {call.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">{call.call_duration}s</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">
                          {call.processing_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[240px]">
                        {tagsLoading ? (
                          <span className="text-xs text-muted-foreground">Loading tags...</span>
                        ) : (
                          <TagListPreview items={callTags} maxVisible={2} title="Call Tags" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 text-xs"
                            disabled={analyzingCalls.has(call.call_id)}
                            onClick={(e) => handleAnalyzeCall(e, call.call_id)}
                          >
                            {analyzingCalls.has(call.call_id)
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><Zap className="w-3.5 h-3.5 mr-1" />Analyze</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => openCallDetailsInNewTab(call.call_id, e)} data-testid="call-row-open-button"                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Time</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Agent</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Caller</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Direction</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Duration</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Language</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Score</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Tags</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></td></tr>
                ) : calls.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-sm text-muted-foreground">No analyzed calls found for current filters.</td></tr>
                ) : calls.map((call) => {
                  const score = call.analysis?.overall_score;
                  const scoreBadge = getScoreBadge(score);
                  const callTags = callTagMap[call.call_id] || [];
                  const tagsLoading = callTagsLoadingKey === displayedCallIdsKey;
                  return (
                    <tr
                      key={call.call_id || call.id}
                      className="border-b border-border/50 hover:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={(e) => openCallDetailsInNewTab(call.call_id, e)}>
                      <td className="px-4 py-3">
                        <span className="text-sm">{call.date}</span>
                        <span className="text-xs text-muted-foreground ml-1">{call.time}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">{call.agent_name}</td>
                      <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{call.client_number}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>
                          {call.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">{call.call_duration}s</td>
                      <td className="px-4 py-3 text-sm">{formatLanguage(call.detected_language)}</td>
                      <td className="px-4 py-3">
                        {score !== null && score !== undefined ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${scoreBadge.cls}`}>
                            {score} - {scoreBadge.label}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-[240px]">
                        {tagsLoading ? (
                          <span className="text-xs text-muted-foreground">Loading tags...</span>
                        ) : (
                          <TagListPreview items={callTags} maxVisible={2} title="Call Tags" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => openCallDetailsInNewTab(call.call_id, e)} data-testid="call-row-open-button">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {displayedPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Page {displayedPage} of {displayedPages} ({displayedTotal} records)</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={displayedPage <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" disabled={displayedPage >= displayedPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
